(function(){
  "use strict";

  var root = document.querySelector(".ptiq-trainer");
  if(!root) return;

  var STORAGE_KEY = "ptiq_inspection_trainer_v1";
  var STEP_COMPLETE_KEY = "ptiq_inspection_complete";
  var REVIEW_REQUEST_KEY = "ptiq_review_requested_v1";
  var REVIEW_DELAY_MS = 1500;
  var reviewRequestScheduled = false;

  var sections = [
    {
      id:"engine",
      title:"Engine Compartment",
      description:"Begin at the front of the vehicle and build a repeatable left-to-right inspection pattern.",
      image:root.dataset.engineImage,
      memory:"Fluids → belts and hoses → steering → suspension → brakes → wheels and tires",
      components:[
        {
          title:"Engine Oil",
          type:"FLUID SYSTEM",
          know:"Engine oil lubricates and protects internal engine components.",
          find:"Locate the dipstick in the engine compartment.",
          say:"The engine oil level is within the safe operating range, with no visible leaks.",
          check:["Level is within the marked safe range","No fresh oil leaks","Dipstick and tube are secure"],
          why:"Low oil or active leaks can cause severe engine damage and unsafe operation.",
          coach:"Name the fluid, confirm the level, and mention leaks."
        },
        {
          title:"Coolant Reservoir",
          type:"COOLING SYSTEM",
          know:"Coolant controls engine temperature and helps prevent overheating.",
          find:"Locate the translucent coolant reservoir or the manufacturer-approved sight area.",
          say:"The coolant level is adequate, the reservoir is secure, and there are no visible leaks.",
          check:["Proper fluid level","Reservoir is secure","Cap is secure","No leaks or damage"],
          why:"Loss of coolant can cause overheating, engine damage, and breakdown.",
          coach:"Never remove a hot pressurized cap during an inspection."
        },
        {
          title:"Belts and Hoses",
          type:"ENGINE ACCESSORIES",
          know:"Belts drive engine accessories and hoses carry critical fluids or air.",
          find:"Inspect visible belts, hoses, clamps, and connection points.",
          say:"The belts and hoses are secure, not cracked, frayed, cut, swollen, or leaking.",
          check:["Belts have proper tension","No excessive cracks or fraying","Hoses are not swollen or cut","Clamps and connections are secure"],
          why:"A failed belt or hose can disable steering, charging, cooling, or other essential systems.",
          coach:"Use both condition and security language."
        },
        {
          title:"Steering Gearbox and Linkage",
          type:"STEERING SYSTEM",
          know:"The steering gearbox transfers driver input to the steering linkage.",
          find:"Locate the gearbox on the frame and follow the linkage toward the steering axle.",
          say:"The steering gearbox is properly mounted and secured, not cracked, bent, or broken, and it is not leaking. The linkage is secure with no missing hardware.",
          check:["Gearbox mounting is secure","No cracks or damage","No hydraulic leaks","Linkage and hardware are secure"],
          why:"Steering looseness or failure can cause loss of vehicle control.",
          coach:"Mention the gearbox, linkage, mounting, and leaks."
        },
        {
          title:"Front Suspension",
          type:"SUSPENSION SYSTEM",
          know:"The suspension supports the vehicle and keeps the axle controlled.",
          find:"Inspect springs, hangers, shackles, U-bolts, mounts, and shock absorbers.",
          say:"The suspension components are secure, not cracked, shifted, bent, or broken, with no missing hardware.",
          check:["Springs are aligned and intact","Mounts and hangers are secure","U-bolts are secure","Shock absorber is mounted and not leaking"],
          why:"Suspension failure can affect stability, tire contact, braking, and steering.",
          coach:"Look for shifting, broken leaves, loose mounts, and missing hardware."
        },
        {
          title:"Front Brake Assembly",
          type:"BRAKE SYSTEM",
          know:"The front brake assembly creates stopping force at the wheel end.",
          find:"Inspect visible hoses, chambers, slack adjusters, pushrods, drums or rotors, and linings where accessible.",
          say:"The brake components are secure and not damaged. Hoses are not cut, cracked, worn, or leaking, and the friction components are not dangerously thin or contaminated.",
          check:["Hoses and lines are secure","No audible or visible leaks","Chamber and hardware are secure","Drum or rotor is not cracked","Lining or pad is adequate"],
          why:"Brake defects can increase stopping distance or cause loss of braking control.",
          coach:"Brake items are safety-critical. Be specific."
        },
        {
          title:"Front Wheel and Tire",
          type:"WHEEL END",
          know:"The tire, rim, hub, and lug hardware keep the vehicle supported and rolling safely.",
          find:"Inspect the steer tire, rim, hub seal, valve stem, and lug nuts.",
          say:"The tire is properly inflated with adequate tread and no cuts, bulges, or exposed cord. The rim is not cracked or bent, the lug nuts are secure, and the hub seal is not leaking.",
          check:["Proper inflation","Adequate tread depth","No cuts, bulges, or exposed cord","Rim is not cracked or bent","Lug nuts show no looseness","Hub seal is not leaking"],
          why:"A steer-tire or wheel-end failure can cause sudden loss of control.",
          coach:"Always include tire condition, rim, lugs, and hub."
        }
      ]
    },
    {
      id:"driver-side",
      title:"Driver-Side Walkaround",
      description:"Continue down the driver side using the same top-to-bottom order at every stop.",
      image:root.dataset.driverImage,
      memory:"Mirrors and door → fuel area → frame → drive axle suspension → brakes → wheels and tires",
      components:[
        {
          title:"Mirror, Door, and Steps",
          type:"CAB EXTERIOR",
          know:"These components support visibility, secure entry, and safe access to the cab.",
          find:"Inspect the driver-side mirror assembly, door, hinges, handle, seals, and steps.",
          say:"The mirror, door, and steps are properly mounted and secure, not cracked, bent, broken, or loose, and the door opens and latches properly.",
          check:["Mirror glass is clean and secure","Mounting arms are secure","Door opens and latches","Steps are secure and clear"],
          why:"Poor visibility or unsafe access can lead to falls, collisions, or loss of control.",
          coach:"Do not stop at 'looks good.' Name security and function."
        },
        {
          title:"Fuel Tank and Cap",
          type:"FUEL SYSTEM",
          know:"The fuel tank stores fuel and must remain secure and leak-free.",
          find:"Inspect the tank, straps, cap, lines, and surrounding area.",
          say:"The fuel tank is properly mounted and secured, the cap is tight, and there are no visible leaks or damage.",
          check:["Tank straps are secure","Cap is present and tight","No dents affecting safety","No fuel leaks"],
          why:"Fuel leaks create fire, environmental, and breakdown hazards.",
          coach:"Include straps, cap, condition, and leaks."
        },
        {
          title:"Frame and Crossmembers",
          type:"STRUCTURE",
          know:"The frame supports the cab, drivetrain, suspension, and trailer loads.",
          find:"Inspect visible frame rails, crossmembers, and mounting points.",
          say:"The frame and crossmembers are straight and secure, with no cracks, bends, illegal welds, or missing hardware.",
          check:["No cracks or bends","No unauthorized welds","Crossmembers are secure","No missing bolts"],
          why:"Structural damage can compromise stability and component mounting.",
          coach:"Look carefully near suspension and coupling mounts."
        },
        {
          title:"Drive Axle Suspension",
          type:"SUSPENSION SYSTEM",
          know:"The drive suspension supports rear tractor weight and controls axle movement.",
          find:"Inspect springs or airbags, mounts, torque rods, U-bolts, and shock absorbers.",
          say:"The drive suspension components are secure, not cracked, shifted, bent, broken, or leaking, with no missing hardware.",
          check:["Airbags or springs are intact","Mounts are secure","Torque rods are secure","No air or fluid leaks"],
          why:"Failure can affect traction, stability, braking, and tire wear.",
          coach:"Adapt your wording to the suspension type actually present."
        },
        {
          title:"Drive Axle Brakes",
          type:"BRAKE SYSTEM",
          know:"Drive axle brakes provide major stopping force for the tractor.",
          find:"Inspect chambers, hoses, slack adjusters, pushrods, drums or rotors, and linings where visible.",
          say:"The brake hoses and chambers are secure and not leaking, the slack adjusters and hardware are secure, and the drums or rotors and friction material are in safe condition.",
          check:["No hose damage or leaks","Chambers are secure","Slack adjuster is secure","No cracked drum or rotor","Adequate friction material"],
          why:"A drive-axle brake defect reduces stopping performance and can cause imbalance.",
          coach:"Use the same brake language consistently at each axle."
        },
        {
          title:"Dual Wheels and Tires",
          type:"WHEEL END",
          know:"Dual tires and wheel hardware must carry load evenly without contacting each other.",
          find:"Inspect both tires, the space between them, rims, lugs, valve stems, and hub seal.",
          say:"Both tires are properly inflated, have adequate tread, and show no cuts, bulges, exposed cord, or contact between the duals. The rims, lugs, and hub are secure and leak-free.",
          check:["Both tires are inflated","No debris between duals","No tire contact","Rims are undamaged","Lugs are secure","Hub is not leaking"],
          why:"Dual-tire failure can cause loss of load support, debris hazards, and control problems.",
          coach:"Remember to inspect between the duals."
        }
      ]
    },
    {
      id:"coupling",
      title:"Coupling System",
      description:"Inspect every connection that keeps the tractor and trailer safely joined.",
      image:root.dataset.couplingImage,
      memory:"Air and electrical lines → catwalk and platform → fifth wheel mount → locking jaws → kingpin and apron → clearance",
      components:[
        {
          title:"Air and Electrical Lines",
          type:"CONNECTIONS",
          know:"Air lines control trailer brakes and the electrical line powers trailer lighting and signals.",
          find:"Inspect the lines from the tractor connections to the trailer connections.",
          say:"The air and electrical lines are properly connected, supported, and secure, with no cuts, cracks, abrasions, leaks, exposed wires, or dragging.",
          check:["Connections are locked","Lines are supported","No rubbing or dragging","No air leaks","No exposed wiring"],
          why:"A failed connection can cause trailer brake or lighting failure.",
          coach:"Mention both connection security and line condition."
        },
        {
          title:"Catwalk and Mounting Platform",
          type:"ACCESS AND STRUCTURE",
          know:"The catwalk provides access and the platform supports the fifth wheel assembly.",
          find:"Inspect the catwalk surface and the platform beneath the fifth wheel.",
          say:"The catwalk and mounting platform are secure, not cracked, bent, broken, or excessively contaminated.",
          check:["Surface is secure","No structural cracks","No missing hardware","No severe contamination"],
          why:"Loose or damaged mounting structure can affect coupling security.",
          coach:"Do not confuse the catwalk with the fifth wheel itself."
        },
        {
          title:"Fifth Wheel Mount",
          type:"COUPLING",
          know:"The fifth wheel mount secures the coupling assembly to the tractor frame.",
          find:"Inspect mounting bolts, brackets, welds, and slider locking mechanisms if equipped.",
          say:"The fifth wheel is properly mounted and secured, with no cracks, bends, broken welds, missing hardware, or unlocked slider pins.",
          check:["Mounting bolts are secure","No cracked welds","Slider pins are locked if equipped","No excessive damage"],
          why:"Mount failure can allow trailer separation.",
          coach:"Mounting security is separate from locking-jaw security."
        },
        {
          title:"Locking Jaws and Kingpin",
          type:"COUPLING",
          know:"The jaws lock around the kingpin to secure the trailer.",
          find:"Visually confirm the jaws are fully closed around the kingpin shank.",
          say:"The locking jaws are fully closed around the kingpin shank, with no visible gap, damage, or improper lock position.",
          check:["Jaws fully encircle the shank","Release handle is seated","No visible gap","No damaged components"],
          why:"Improper jaw engagement can cause trailer separation.",
          coach:"Say 'around the shank,' not around the kingpin head."
        },
        {
          title:"Apron and Clearance",
          type:"COUPLING FIT",
          know:"The trailer apron must rest flat on the fifth wheel with safe clearance around the tractor and landing gear.",
          find:"Inspect the mating surfaces and space around the tractor frame, mudflaps, and landing gear.",
          say:"The trailer apron is resting flat on the fifth wheel with no gap, and there is adequate clearance between the tractor, trailer, and landing gear.",
          check:["No gap between apron and fifth wheel","Adequate turning clearance","Landing gear clears tractor frame","No visible damage"],
          why:"Poor fit or inadequate clearance can damage equipment or compromise coupling.",
          coach:"Check both fit and movement clearance."
        }
      ]
    },
    {
      id:"trailer",
      title:"Trailer",
      description:"Continue from the coupling area to the rear, maintaining the same inspection rhythm.",
      image:root.dataset.couplingImage,
      memory:"Front wall → landing gear → frame and crossmembers → tandem and suspension → brakes → wheels and tires → rear doors and lights",
      components:[
        {
          title:"Trailer Front and Header Board",
          type:"TRAILER STRUCTURE",
          know:"The front wall contains and protects the load and must remain structurally sound.",
          find:"Inspect the trailer front wall, upper corners, panels, and visible supports.",
          say:"The trailer front and header board are secure and not cracked, bent, broken, punctured, or missing fasteners.",
          check:["Panels are secure","No major holes or cracks","Corner structure is intact","No loose material"],
          why:"Structural damage can compromise cargo containment and trailer integrity.",
          coach:"Adjust wording to the trailer type."
        },
        {
          title:"Landing Gear",
          type:"TRAILER SUPPORT",
          know:"Landing gear supports the trailer when it is not attached to the tractor.",
          find:"Inspect both legs, braces, feet, crank handle, and mounting points.",
          say:"The landing gear is fully raised, properly mounted and secured, with the crank handle secured and no cracked, bent, or broken components.",
          check:["Both legs fully raised","Bracing is secure","Feet are not damaged","Crank handle is stored"],
          why:"Unsecured or lowered landing gear can strike the road or fail during uncoupling.",
          coach:"Include position, security, and condition."
        },
        {
          title:"Trailer Frame and Crossmembers",
          type:"STRUCTURE",
          know:"The trailer frame carries the cargo and connects suspension and body systems.",
          find:"Inspect visible frame rails, crossmembers, floor supports, and mounting points.",
          say:"The trailer frame and crossmembers are straight and secure, with no cracks, bends, broken welds, or missing hardware.",
          check:["No structural cracks","No severe bends","Crossmembers are secure","No missing fasteners"],
          why:"Frame damage can lead to load shift or structural failure.",
          coach:"Pay attention near landing gear and tandem mounts."
        },
        {
          title:"Tandem Release and Suspension",
          type:"AXLE POSITION AND SUPPORT",
          know:"The tandem lock secures axle position and the suspension supports trailer weight.",
          find:"Inspect release handle, locking pins, rails, airbags or springs, mounts, and torque rods.",
          say:"The tandem release is secured and all locking pins are fully engaged. Suspension components are secure, not cracked, shifted, bent, broken, or leaking.",
          check:["Release handle is secured","All pins are engaged","Rails are not damaged","Airbags or springs are intact","Mounts are secure"],
          why:"Unlocked tandems or failed suspension can cause axle movement and loss of stability.",
          coach:"Visually verify every locking pin."
        },
        {
          title:"Trailer Brakes",
          type:"BRAKE SYSTEM",
          know:"Trailer brakes provide stopping force and must operate evenly across the trailer axles.",
          find:"Inspect hoses, chambers, slack adjusters, pushrods, drums or rotors, and linings.",
          say:"The trailer brake components are secure and not damaged or leaking, with safe drums or rotors and adequate friction material.",
          check:["No hose damage or leaks","Chambers are secure","Slack adjusters are secure","No cracked drums or rotors","Adequate lining or pad"],
          why:"Trailer brake defects increase stopping distance and instability.",
          coach:"Repeat the same complete brake pattern at each axle."
        },
        {
          title:"Trailer Wheels and Tires",
          type:"WHEEL END",
          know:"Trailer wheel ends carry cargo weight and must remain secure and properly inflated.",
          find:"Inspect all tires, rims, lugs, valve stems, spacing, and hub seals.",
          say:"The trailer tires are properly inflated with adequate tread and no cuts, bulges, exposed cord, or contact. Rims and lugs are secure, and the hubs are not leaking.",
          check:["Proper inflation","Adequate tread","No tire damage","No dual contact","Rims and lugs secure","No hub leaks"],
          why:"Wheel-end failures can cause crashes, fires, or road debris.",
          coach:"Inspect inside tires and between duals."
        },
        {
          title:"Rear Doors, Lights, and Reflectors",
          type:"REAR SAFETY",
          know:"Rear structure contains cargo while lights and reflectors communicate vehicle position and movement.",
          find:"Inspect doors, hinges, latches, seals, lights, reflectors, and reflective tape.",
          say:"The rear doors, hinges, and latches are secure and undamaged. Lights, reflectors, and reflective tape are clean, properly colored, and operating or visible.",
          check:["Doors close and latch","Hinges are secure","Lights are correct and working","Reflectors and tape are visible"],
          why:"Cargo loss and poor visibility create major roadway hazards.",
          coach:"Include both cargo security and visibility."
        }
      ]
    },
    {
      id:"in-cab",
      title:"In-Cab Inspection",
      description:"Move into the cab and verify controls, gauges, visibility, and required safety equipment.",
      image:root.dataset.inCabImage,
      memory:"Seat belt and mirrors → safety equipment → controls → gauges and warning devices → lights and accessories",
      components:[
        {
          title:"Seat Belt and Mirrors",
          type:"DRIVER RESTRAINT AND VISIBILITY",
          know:"The seat belt protects the driver and mirrors provide essential observation zones.",
          find:"Inspect the belt, latch, anchor points, and all mirrors from the driver seat.",
          say:"The seat belt is securely mounted, not cut, frayed, or damaged, and it latches properly. Mirrors are clean, secure, and adjusted for safe visibility.",
          check:["Belt is not damaged","Latch works","Anchors are secure","Mirrors are clean and adjusted"],
          why:"Restraint and visibility are essential to safe vehicle control.",
          coach:"Physically test the latch."
        },
        {
          title:"Safety and Emergency Equipment",
          type:"REQUIRED EQUIPMENT",
          know:"Emergency equipment helps protect the driver and public during breakdowns or incidents.",
          find:"Locate the fire extinguisher, warning triangles, and spare fuses or required circuit protection.",
          say:"The required emergency equipment is present, accessible, properly secured, and in serviceable condition.",
          check:["Fire extinguisher is charged and secure","Warning devices are present","Spare fuses or circuit protection are available as required"],
          why:"Missing emergency equipment can worsen roadside hazards.",
          coach:"Requirements can vary by vehicle equipment; verify current rules."
        },
        {
          title:"Gauges and Warning Indicators",
          type:"INSTRUMENTATION",
          know:"Gauges and warning lights communicate vehicle operating condition.",
          find:"Observe oil pressure, coolant temperature, voltage, air pressure, and warning indicators.",
          say:"The gauges rise to normal operating ranges, and warning indicators illuminate during the check and then extinguish as appropriate.",
          check:["Oil pressure rises","Temperature remains normal","Voltage is normal","Air pressure builds","Warning lights self-test"],
          why:"Abnormal readings can signal unsafe mechanical conditions.",
          coach:"Do not claim an exact normal range unless verified for the vehicle."
        },
        {
          title:"Steering, Horns, and Wipers",
          type:"DRIVER CONTROLS",
          know:"These controls support direction, warning, and visibility.",
          find:"Check steering wheel play, city and air horns, wipers, and washer operation.",
          say:"The steering has no excessive play, both horns operate, and the wipers and washers function properly with secure blades.",
          check:["No excessive steering play","City horn works","Air horn works if equipped","Wipers and washers work"],
          why:"Control or visibility failure can cause immediate driving danger.",
          coach:"Use the vehicle manufacturer's steering-play limits when known."
        },
        {
          title:"Lighting Controls",
          type:"VISIBILITY AND COMMUNICATION",
          know:"Lighting controls operate the systems that make the vehicle visible and communicate movement.",
          find:"Operate headlights, high beams, turn signals, four-way flashers, clearance lights, and brake-related indicators.",
          say:"All required lighting controls and indicators operate properly, with correct color and no broken or missing lenses.",
          check:["Headlights and high beams work","Turn signals work","Four-way flashers work","Clearance lights and indicators work"],
          why:"Lighting failure reduces visibility and communication with other road users.",
          coach:"Use a helper or reflection when checking exterior lights."
        }
      ]
    },
    {
      id:"air-brakes",
      title:"Air Brake Test",
      description:"Finish with a controlled air-brake procedure. Follow your verified state and vehicle sequence.",
      image:root.dataset.inCabImage,
      memory:"Build pressure → governor check → static/applied leak checks → low-air warning → spring brakes → parking and service brakes",
      components:[
        {
          title:"Air Pressure Build and Governor",
          type:"AIR SYSTEM",
          know:"The compressor must build air pressure and the governor must control cut-in and cut-out.",
          find:"Observe both air gauges while the engine runs and pressure builds.",
          say:"Air pressure builds at an acceptable rate, both gauges rise together, and governor cut-in and cut-out occur within the verified operating range for this vehicle.",
          check:["Both gauges respond","Pressure builds steadily","Cut-in occurs appropriately","Cut-out occurs appropriately"],
          why:"Failure to build or regulate pressure can disable the braking system.",
          coach:"Use verified thresholds for the selected state and vehicle."
        },
        {
          title:"Static and Applied Leak Checks",
          type:"AIR SYSTEM INTEGRITY",
          know:"Leak checks confirm the system can hold pressure without excessive loss.",
          find:"With the vehicle secured and pressure stabilized, perform the applicable static and applied checks.",
          say:"The air-pressure loss remains within the verified allowable limit during the static and applied leak checks.",
          check:["Vehicle is secured","Pressure is stabilized","Correct brake position is used","Loss remains within verified limits"],
          why:"Excessive leakage can reduce braking capacity or cause spring-brake application.",
          coach:"Do not publish one universal threshold; state and combination requirements vary."
        },
        {
          title:"Low-Air Warning",
          type:"WARNING SYSTEM",
          know:"The warning must activate before air pressure becomes dangerously low.",
          find:"Reduce air pressure in a controlled manner and observe the warning device.",
          say:"The low-air warning activates before pressure falls below the verified required threshold.",
          check:["Visual or audible warning activates","Warning occurs before unsafe pressure","Both gauges are monitored"],
          why:"The warning gives the driver time to stop before braking ability is compromised.",
          coach:"State the verified threshold used by the selected test path."
        },
        {
          title:"Spring-Brake Activation",
          type:"EMERGENCY BRAKING",
          know:"Spring brakes apply automatically if air pressure drops too low.",
          find:"Continue reducing pressure while observing the parking-brake controls.",
          say:"The spring brakes activate within the verified required pressure range as air pressure decreases.",
          check:["Controls respond appropriately","Activation occurs within verified range","Vehicle remains secured"],
          why:"Spring brakes provide emergency stopping and parking force when pressure is lost.",
          coach:"Use controlled conditions and never perform the test unsafely."
        },
        {
          title:"Parking and Service Brake Checks",
          type:"FUNCTIONAL BRAKE TEST",
          know:"These checks confirm the vehicle holds when parked and stops without pulling during movement.",
          find:"Perform the parking-brake hold checks and a low-speed service-brake check in a safe area.",
          say:"The tractor and trailer parking brakes hold as required, and the service brake stops the vehicle smoothly without pulling to either side or producing unusual response.",
          check:["Tractor parking brake holds","Trailer parking brake holds when applicable","Service brake stops smoothly","No pulling or unusual response"],
          why:"Functional checks confirm actual braking performance before road operation.",
          coach:"Only perform movement checks in a safe, controlled area."
        }
      ]
    }
  ];

  var state = {
    sectionIndex:0,
    componentIndex:0,
    complete:{},
    evidence:{},
    practiceSeconds:{}
  };

  var practiceTimer = null;
  var practiceStartedAt = null;

  function loadState(){
    try{
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if(saved && typeof saved === "object"){
        state.sectionIndex = Number.isInteger(saved.sectionIndex) ? saved.sectionIndex : 0;
        state.componentIndex = Number.isInteger(saved.componentIndex) ? saved.componentIndex : 0;
        state.complete = saved.complete || {};
        state.evidence = saved.evidence || {};
        state.practiceSeconds = saved.practiceSeconds || {};
      }
    }catch(error){}
    clampState();
  }

  function saveState(){
    try{ localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }catch(error){}
  }

  function clampState(){
    if(state.sectionIndex < 0 || state.sectionIndex >= sections.length) state.sectionIndex = 0;
    var count = sections[state.sectionIndex].components.length;
    if(state.componentIndex < 0 || state.componentIndex >= count) state.componentIndex = 0;
  }

  function keyFor(sectionIndex, componentIndex){
    return sections[sectionIndex].id + ":" + componentIndex;
  }

  function currentSection(){ return sections[state.sectionIndex]; }
  function currentComponent(){ return currentSection().components[state.componentIndex]; }
  function currentKey(){ return keyFor(state.sectionIndex, state.componentIndex); }

  function totalComponents(){
    return sections.reduce(function(sum, section){ return sum + section.components.length; }, 0);
  }

  function completedCount(){
    return Object.keys(state.complete).filter(function(key){ return state.complete[key] === true; }).length;
  }

  function sectionCompletedCount(sectionIndex){
    return sections[sectionIndex].components.reduce(function(sum, _, componentIndex){
      return sum + (state.complete[keyFor(sectionIndex, componentIndex)] ? 1 : 0);
    }, 0);
  }

  function isSectionComplete(sectionIndex){
    return sectionCompletedCount(sectionIndex) === sections[sectionIndex].components.length;
  }

  function allComplete(){
    return completedCount() === totalComponents();
  }

  var els = {
    sectionNav:root.querySelector("[data-section-nav]"),
    sectionCount:root.querySelector("[data-section-count]"),
    sectionLabel:root.querySelector("[data-section-label]"),
    sectionTitle:root.querySelector("[data-section-title]"),
    sectionDescription:root.querySelector("[data-section-description]"),
    sectionPercent:root.querySelector("[data-section-percent]"),
    sectionImage:root.querySelector("[data-section-image]"),
    sectionMemory:root.querySelector("[data-section-memory]"),
    componentNumber:root.querySelector("[data-component-number]"),
    componentType:root.querySelector("[data-component-type]"),
    componentTitle:root.querySelector("[data-component-title]"),
    componentStatus:root.querySelector("[data-component-status]"),
    componentKnow:root.querySelector("[data-component-know]"),
    componentFind:root.querySelector("[data-component-find]"),
    componentSay:root.querySelector("[data-component-say]"),
    componentCheck:root.querySelector("[data-component-check]"),
    componentWhy:root.querySelector("[data-component-why]"),
    componentCoach:root.querySelector("[data-component-coach]"),
    overallPercent:root.querySelector("[data-overall-percent]"),
    overallFill:root.querySelector("[data-overall-fill]"),
    overallRing:root.querySelector("[data-overall-ring]"),
    progressTitle:root.querySelector("[data-progress-title]"),
    progressCopy:root.querySelector("[data-progress-copy]"),
    prev:root.querySelector("[data-prev-component]"),
    next:root.querySelector("[data-next-component]"),
    mark:root.querySelector("[data-mark-complete]"),
    sectionComplete:root.querySelector("[data-section-complete]"),
    completeTitle:root.querySelector("[data-complete-title]"),
    nextSection:root.querySelector("[data-next-section]"),
    finish:root.querySelector("[data-finish-panel]"),
    evidenceArea:root.querySelector("[data-evidence-area]"),
    evidencePreview:root.querySelector("[data-evidence-preview]"),
    evidenceName:root.querySelector("[data-evidence-name]"),
    cameraInput:root.querySelector("#ptiq-camera-input"),
    practicePanel:root.querySelector("[data-practice-panel]"),
    practiceTimer:root.querySelector("[data-practice-timer]"),
    practiceStatus:root.querySelector("[data-practice-status]")
  };

  function renderNav(){
    els.sectionNav.innerHTML = "";
    sections.forEach(function(section, index){
      var button = document.createElement("button");
      button.type = "button";
      button.className = "ptiq-section-button" +
        (index === state.sectionIndex ? " is-current" : "") +
        (isSectionComplete(index) ? " is-complete" : "");
      button.innerHTML =
        "<span>" + String(index + 1).padStart(2,"0") + "</span>" +
        "<span><strong>" + section.title + "</strong><small>" +
        sectionCompletedCount(index) + " of " + section.components.length + " complete</small></span>" +
        "<span>" + (isSectionComplete(index) ? "✓" : "→") + "</span>";
      button.addEventListener("click", function(){
        stopPractice();
        state.sectionIndex = index;
        state.componentIndex = 0;
        saveState();
        render();
        root.querySelector("#trainer").scrollIntoView({behavior:"smooth",block:"start"});
      });
      els.sectionNav.appendChild(button);
    });
    var doneSections = sections.filter(function(_, i){ return isSectionComplete(i); }).length;
    els.sectionCount.textContent = doneSections + " of " + sections.length + " sections";
  }

  function requestAppReviewIfEligible(){
    if(reviewRequestScheduled) return;

    try{
      if(localStorage.getItem(REVIEW_REQUEST_KEY) === "1") return;
    }catch(error){}

    reviewRequestScheduled = true;

    window.setTimeout(function(){
      try{
        var nativeBridge = window.PreTripIQNative;
        if(!nativeBridge || typeof nativeBridge.requestReview !== "function"){
          reviewRequestScheduled = false;
          return;
        }

        nativeBridge.requestReview();

        try{
          localStorage.setItem(REVIEW_REQUEST_KEY,"1");
        }catch(error){}
      }catch(error){
        reviewRequestScheduled = false;
      }
    }, REVIEW_DELAY_MS);
  }

  function renderOverall(){
    var complete = completedCount();
    var total = totalComponents();
    var percent = Math.round((complete / total) * 100);
    els.overallPercent.textContent = percent + "%";
    els.overallFill.style.width = percent + "%";
    els.overallRing.style.background =
      "radial-gradient(circle at center,#0b1c27 58%,transparent 60%)," +
      "conic-gradient(var(--cyan) 0 " + percent + "%,rgba(255,255,255,.08) " + percent + "% 100%)";

    if(percent === 0){
      els.progressTitle.textContent = "Ready to begin";
      els.progressCopy.textContent = "Start with the Engine Compartment and follow the same order every time.";
    }else if(percent < 50){
      els.progressTitle.textContent = "You are building the sequence";
      els.progressCopy.textContent = complete + " of " + total + " components complete.";
    }else if(percent < 100){
      els.progressTitle.textContent = "More than halfway through";
      els.progressCopy.textContent = "Keep the same pace and finish every safety-critical section.";
    }else{
      els.progressTitle.textContent = "Inspection IQ complete";
      els.progressCopy.textContent = "Continue to Voice Practice IQ and strengthen your spoken inspection.";
    }

    els.finish.hidden = !allComplete();
    if(allComplete()){
      try{ localStorage.setItem(STEP_COMPLETE_KEY,"1"); }catch(error){}
      requestAppReviewIfEligible();
    }
  }

  function renderEvidence(){
    var evidence = state.evidence[currentKey()];
    if(evidence && evidence.dataUrl){
      els.evidenceArea.hidden = false;
      els.evidencePreview.src = evidence.dataUrl;
      els.evidenceName.textContent = evidence.name || "Inspection photo";
    }else{
      els.evidenceArea.hidden = true;
      els.evidencePreview.removeAttribute("src");
    }
  }

  function renderPractice(){
    var seconds = Number(state.practiceSeconds[currentKey()] || 0);
    els.practiceTimer.textContent = formatTime(seconds);
    els.practiceStatus.textContent = seconds > 0 ? "Last practice: " + formatTime(seconds) : "Start a timed speaking practice";
  }

  function render(){
    clampState();
    var section = currentSection();
    var component = currentComponent();
    var sectionDone = sectionCompletedCount(state.sectionIndex);
    var sectionPercent = Math.round((sectionDone / section.components.length) * 100);
    var complete = !!state.complete[currentKey()];

    renderNav();
    renderOverall();

    els.sectionLabel.textContent = "SECTION " + (state.sectionIndex + 1) + " OF " + sections.length;
    els.sectionTitle.textContent = section.title;
    els.sectionDescription.textContent = section.description;
    els.sectionPercent.textContent = sectionPercent + "%";
    els.sectionImage.src = section.image;
    els.sectionImage.alt = section.title + " inspection training";
    els.sectionMemory.textContent = section.memory;

    els.componentNumber.textContent = String(state.componentIndex + 1).padStart(2,"0");
    els.componentType.textContent = component.type;
    els.componentTitle.textContent = component.title;
    els.componentStatus.textContent = complete ? "Complete" : "Not complete";
    els.componentStatus.className = "ptiq-trainer-component__status" + (complete ? " is-complete" : "");
    els.componentKnow.textContent = component.know;
    els.componentFind.textContent = component.find;
    els.componentSay.textContent = component.say;
    els.componentWhy.textContent = component.why;
    els.componentCoach.textContent = component.coach;
    els.componentCheck.innerHTML = component.check.map(function(item){ return "<li>" + item + "</li>"; }).join("");

    els.mark.textContent = complete ? "Completed ✓" : "Mark Component Complete";
    els.prev.disabled = state.sectionIndex === 0 && state.componentIndex === 0;
    els.next.textContent =
      (state.sectionIndex === sections.length - 1 && state.componentIndex === section.components.length - 1)
      ? "Finish Inspection →"
      : "Next Component →";

    var sectionComplete = isSectionComplete(state.sectionIndex);
    els.sectionComplete.hidden = !sectionComplete || state.sectionIndex === sections.length - 1;
    els.completeTitle.textContent = section.title + " complete";

    renderEvidence();
    renderPractice();
    saveState();
  }

  function nextComponent(){
    stopPractice();
    var section = currentSection();
    if(state.componentIndex < section.components.length - 1){
      state.componentIndex += 1;
    }else if(state.sectionIndex < sections.length - 1){
      state.sectionIndex += 1;
      state.componentIndex = 0;
    }
    saveState();
    render();
  }

  function prevComponent(){
    stopPractice();
    if(state.componentIndex > 0){
      state.componentIndex -= 1;
    }else if(state.sectionIndex > 0){
      state.sectionIndex -= 1;
      state.componentIndex = sections[state.sectionIndex].components.length - 1;
    }
    saveState();
    render();
  }

  function speakPrompt(){
    var text = currentComponent().say;
    try{
      var nativeBridge = window.PreTripIQNative;
      if(nativeBridge && typeof nativeBridge.speak === "function"){
        var accepted = nativeBridge.speak(text);
        if(accepted === true) return;
      }
    }catch(error){}

    if("speechSynthesis" in window){
      window.speechSynthesis.cancel();
      var utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }else{
      alert("Voice playback is not available on this device.");
    }
  }

  function formatTime(seconds){
    seconds = Math.max(0, Number(seconds) || 0);
    var minutes = Math.floor(seconds / 60);
    var remaining = seconds % 60;
    return String(minutes).padStart(2,"0") + ":" + String(remaining).padStart(2,"0");
  }

  function startPractice(){
    if(practiceTimer){
      stopPractice();
      return;
    }
    practiceStartedAt = Date.now();
    els.practicePanel.hidden = false;
    els.practiceStatus.textContent = "Practice in progress";
    practiceTimer = setInterval(function(){
      var seconds = Math.floor((Date.now() - practiceStartedAt) / 1000);
      els.practiceTimer.textContent = formatTime(seconds);
    }, 250);
  }

  function stopPractice(){
    if(!practiceTimer) return;
    clearInterval(practiceTimer);
    practiceTimer = null;
    var seconds = Math.max(1, Math.floor((Date.now() - practiceStartedAt) / 1000));
    state.practiceSeconds[currentKey()] = seconds;
    practiceStartedAt = null;
    els.practicePanel.hidden = true;
    saveState();
    renderPractice();
  }

  root.querySelector("[data-start-session]").addEventListener("click", function(){
    root.querySelector("#trainer").scrollIntoView({behavior:"smooth",block:"start"});
  });

  els.prev.addEventListener("click", prevComponent);
  els.next.addEventListener("click", nextComponent);

  els.mark.addEventListener("click", function(){
    state.complete[currentKey()] = !state.complete[currentKey()];
    saveState();
    render();
  });

  els.nextSection.addEventListener("click", function(){
    stopPractice();
    if(state.sectionIndex < sections.length - 1){
      state.sectionIndex += 1;
      state.componentIndex = 0;
      saveState();
      render();
    }
  });

  root.querySelector("[data-speak]").addEventListener("click", speakPrompt);
  root.querySelector("[data-practice-toggle]").addEventListener("click", startPractice);
  root.querySelector("[data-stop-practice]").addEventListener("click", stopPractice);

  els.cameraInput.addEventListener("change", function(event){
    var file = event.target.files && event.target.files[0];
    if(!file) return;
    if(!file.type || file.type.indexOf("image/") !== 0){
      alert("Please choose an image file.");
      return;
    }
    if(file.size > 8 * 1024 * 1024){
      alert("Please choose an image smaller than 8 MB.");
      return;
    }

    var reader = new FileReader();
    reader.onload = function(){
      state.evidence[currentKey()] = {
        dataUrl:String(reader.result || ""),
        name:file.name || "Inspection photo",
        savedAt:Date.now()
      };
      saveState();
      renderEvidence();
    };
    reader.onerror = function(){ alert("The image could not be loaded. Please try again."); };
    reader.readAsDataURL(file);
    event.target.value = "";
  });

  root.querySelector("[data-remove-evidence]").addEventListener("click", function(){
    delete state.evidence[currentKey()];
    saveState();
    renderEvidence();
  });

  root.querySelector("[data-reset-progress]").addEventListener("click", function(){
    if(!confirm("Reset all Inspection IQ progress, practice timers, and saved photos on this device?")) return;
    stopPractice();
    state = {sectionIndex:0,componentIndex:0,complete:{},evidence:{},practiceSeconds:{}};
    try{
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STEP_COMPLETE_KEY);
    }catch(error){}
    render();
  });

  loadState();
  render();
})();
